import {
  createContext,
  type HTMLAttributes,
  type KeyboardEvent,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';

import {
  itemIsSelectedSelector,
  resetSelectedItemsAction,
  selectableItemsSelector,
  selectItemAction,
} from '@air/redux-selected-items';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { canChangeVersionDetails } from '~/utils/permissions/assetPermissions';
import { canChangeBoardDetails } from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';
import {
  blurRowElement,
  DATA_ATTRIBUTE,
  EDITABLE_TABLE_TITLE_INPUT_ID,
  focusRowElement,
  getRowElement,
  selectRowInputText,
} from './rowUtils';

type RenamingObserver = (isRenaming: boolean) => void;

type RenamingContextValue = {
  subscribe: (id: string, observer: RenamingObserver) => { unsubscribe: () => void };
  setIsRenaming: (id: string | null) => void;
  getKeyboardListenerProps: (id: string) => HTMLAttributes<HTMLDivElement>;
};

const defaultValue: RenamingContextValue = {
  subscribe: () => ({ unsubscribe: () => null }),
  setIsRenaming: () => null,
  getKeyboardListenerProps: () => ({}),
};

const TRIGGER_RENAME_ATTRIBUTE = 'data-trigger-rename';

const KEY_NAME_MAP: Record<string, KeyboardEvent['key']> = {
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ENTER: 'Enter',
};

export const RenamingContext = createContext<RenamingContextValue>(defaultValue);

export const renamableElementProps = {
  [TRIGGER_RENAME_ATTRIBUTE]: true,
};

export type TableViewItemRenamingProviderProps = {
  children: ReactNode;
};

export const TableViewItemRenamingProvider = memo(({ children }: TableViewItemRenamingProviderProps) => {
  const renamingItemIdRef = useRef<string | null>(null);
  const selectedItemRef = useRef<string | null>(null);
  const observersRef = useRef<Map<string, Set<RenamingObserver>>>(new Map());
  const dispatch = useDispatch();
  const store = useAirStore();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();

  const notifyObservers = useCallback((id: string, isRenaming: boolean) => {
    const observers = observersRef.current.get(id);
    observers?.forEach((observer) => observer(isRenaming));
  }, []);

  const setIsRenaming = useCallback(
    (id: string | null) => {
      const prevId = renamingItemIdRef.current;

      // Notify previous item's observers that it's no longer being renamed
      if (prevId) {
        notifyObservers(prevId, false);
        focusRowElement(prevId);
      }

      // Notify new item's observers that it is being renamed
      if (id) {
        notifyObservers(id, true);
        blurRowElement(id);
      }

      renamingItemIdRef.current = id;
    },
    [notifyObservers],
  );

  // Check if the row is focused by tabbing
  const hasFocus = useCallback((id: string) => {
    const rowElement = getRowElement(id);
    if (!rowElement) {
      return false;
    }

    const activeElement = document.activeElement;
    if (!activeElement) {
      return false;
    }

    return rowElement.contains(activeElement);
  }, []);

  const handleKeyDown = useCallback(
    (id: string) => (e: KeyboardEvent<HTMLDivElement>) => {
      const state = store.getState();
      const isSelected = itemIsSelectedSelector(state, id);
      const selectableItems = selectableItemsSelector(state);
      const target = e.target as HTMLElement;

      // Reference
      // - isSelected: true if the row is selected by clicking
      // - hasFocus(id): true if the row is focused by tabbing

      if (e.key === KEY_NAME_MAP.ARROW_UP || e.key === KEY_NAME_MAP.ARROW_DOWN) {
        e.preventDefault();
        e.stopPropagation();

        if (!target.hasAttribute(TRIGGER_RENAME_ATTRIBUTE)) {
          return;
        }

        if ((isSelected || hasFocus(id)) && !renamingItemIdRef.current) {
          const currentIndex = selectableItems.findIndex((item) => item.id === id);

          if (currentIndex === -1) {
            return;
          }

          let targetIndex: number;
          if (e.key === KEY_NAME_MAP.ARROW_UP) {
            // Stop at the top of the list
            targetIndex = currentIndex === 0 ? 0 : currentIndex - 1;
          } else {
            // Stop at the bottom of the list
            targetIndex = currentIndex === selectableItems.length - 1 ? currentIndex : currentIndex + 1;
          }

          const targetItem = selectableItems[targetIndex];

          // If the row is selected by clicking, reset the selected items
          // and change selected row to the row navigated to by the up/down arrow keys
          if (isSelected) {
            batch(() => {
              dispatch(resetSelectedItemsAction());
              dispatch(selectItemAction({ item: targetItem }));
            });
          }

          // If the row is focused by tabbing
          // change the focus to the row navigated to by the up/down arrow keys
          if (hasFocus(id)) {
            const targetRow = getRowElement(targetItem.id);
            if (targetRow) {
              targetRow.focus();
            }
          }
        }
      } else if (e.key === KEY_NAME_MAP.ENTER) {
        e.preventDefault();
        e.stopPropagation();

        if (!target.hasAttribute(TRIGGER_RENAME_ATTRIBUTE)) {
          return;
        }

        if (isSelected || hasFocus(id)) {
          selectedItemRef.current = id;
          const item = selectableItems.find((item) => item.id === id);

          if (item?.type === 'asset' || item?.type === 'file') {
            const clipPermissions = getClipPermissions(item.id);
            if (canChangeVersionDetails(clipPermissions)) {
              setIsRenaming(id);
              target.blur();
              selectRowInputText(EDITABLE_TABLE_TITLE_INPUT_ID);
            }
          } else if (item?.type === 'board') {
            const boardPermissions = getBoardPermissions(item.id);
            if (canChangeBoardDetails(boardPermissions)) {
              setIsRenaming(id);
              target.blur();
              selectRowInputText(EDITABLE_TABLE_TITLE_INPUT_ID);
            }
          }
        }
      }
    },
    [store, dispatch, setIsRenaming, getClipPermissions, getBoardPermissions, hasFocus],
  );

  const getKeyboardListenerProps = useCallback(
    (id: string) => ({
      onKeyDown: handleKeyDown(id),
      [DATA_ATTRIBUTE]: id,
      ...renamableElementProps,
    }),
    [handleKeyDown],
  );

  const subscribe = useCallback((id: string, observer: RenamingObserver) => {
    if (!observersRef.current.has(id)) {
      observersRef.current.set(id, new Set());
    }
    const observers = observersRef.current.get(id)!;
    observers.add(observer);

    // Initial state notification
    observer(renamingItemIdRef.current === id);

    return {
      unsubscribe: () => {
        const observers = observersRef.current.get(id);
        if (observers) {
          observers.delete(observer);
          if (observers.size === 0) {
            observersRef.current.delete(id);
          }
        }
      },
    };
  }, []);

  const value = useMemo(
    () => ({ subscribe, setIsRenaming, getKeyboardListenerProps }),
    [subscribe, setIsRenaming, getKeyboardListenerProps],
  );

  return <RenamingContext.Provider value={value}>{children}</RenamingContext.Provider>;
});

TableViewItemRenamingProvider.displayName = 'TableViewItemRenamingProvider';

// Hook for components to subscribe to renaming state changes
export const useIsItemRenaming = (id: string) => {
  const context = useContext(RenamingContext);
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    const { unsubscribe } = context.subscribe(id, setIsRenaming);
    return unsubscribe;
  }, [context, id]);

  return isRenaming;
};

export const useRenamingContext = () => {
  const context = useContext(RenamingContext);
  if (!context) {
    const error = new Error('useRenamingContext must be used within a TableViewItemRenamingProvider');

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error.message,
      });
    }
  }

  return context;
};
